import { createSelector } from '@ngrx/store';
import { BookedVoucherCountSortedByVoucherType, BookingTariff } from '@products/models/booking.model';
import { TariffPersonalizationType } from '@products/models/tariff.model';
import { VoucherType } from '@products/models/voucher.model';
import { getAllBookedTariffs } from '@store/products/booking/product/product-booking.selectors';

/**
 * Get booked voucher tariffs
 * @description Get all booked vouchers form product bookings
 * @returns Booked voucher tariffs or empty array
 */
export const getVoucherTariffBookings = createSelector(getAllBookedTariffs, (bookedTariffs: BookingTariff[]) =>
  bookedTariffs.filter((bookedTariff) => !!bookedTariff.voucherCode)
);

/**
 * Get booked promo code vouchers
 * @description Get booked promo code vouchers from product bookings
 * @returns Booked promo code voucher tariffs or empty array
 */
export const getBookedPromoCodeVoucherTariffs = createSelector(
  getVoucherTariffBookings,
  (bookedVoucherTariffs: BookingTariff[]) =>
    bookedVoucherTariffs.filter((bookedVoucherTariff) => bookedVoucherTariff.voucherType === VoucherType.PromoCode)
);

/**
 * Get booked promo code vouchers count
 * @description Get booked promo code vouchers count from product bookings
 * @returns Booked promo code voucher tariffs count
 */
export const getBookedPromoCodeVoucherTariffsCount = createSelector(
  getBookedPromoCodeVoucherTariffs,
  (bookedPromoCodeVoucherTariffs: BookingTariff[]) =>
    bookedPromoCodeVoucherTariffs.reduce(
      (numberOfBookedPromoCodeVoucherTariffs, bookedPromoCodeVoucherTariff) =>
        numberOfBookedPromoCodeVoucherTariffs + bookedPromoCodeVoucherTariff.count,
      0
    )
);

/**
 * Get booked limited promo code vouchers
 * @description Get booked limited promo code vouchers from product bookings
 * @returns Booked limited promo code voucher tariffs or empty array
 */
export const getBookedLimitedPromoCodeVoucherTariffs = createSelector(
  getVoucherTariffBookings,
  (bookedVoucherTariffs: BookingTariff[]) =>
    bookedVoucherTariffs.filter(
      (bookedVoucherTariff) => bookedVoucherTariff.voucherType === VoucherType.LimiterPromoCode
    )
);

/**
 * Get booked limited promo code vouchers count
 * @description Get booked limited promo code vouchers count from product bookings
 * @returns booked limited promo code voucher tariffs count
 */
export const getBookedLimitedPromoCodeVoucherTariffsCount = createSelector(
  getBookedLimitedPromoCodeVoucherTariffs,
  (bookedLimitedPromoCodeVoucherTariffs: BookingTariff[]) =>
    bookedLimitedPromoCodeVoucherTariffs.reduce(
      (numberOfBookedLimitedPromoCodeVoucherTariffs, bookedLimitedPromoCodeVoucherTariff) =>
        numberOfBookedLimitedPromoCodeVoucherTariffs + bookedLimitedPromoCodeVoucherTariff.count,
      0
    )
);

/**
 * Get booked one time vouchers
 * @description Get booked one time vouchers from product bookings
 * @returns Booked one time voucher tariffs or empty array
 */
export const getBookedOneTimeVoucherTariffs = createSelector(
  getVoucherTariffBookings,
  (bookedVoucherTariffs: BookingTariff[]) =>
    bookedVoucherTariffs.filter((bookedVoucherTariff) => bookedVoucherTariff.voucherType === VoucherType.OneTimeVoucher)
);

/**
 * Get booked one time vouchers count
 * @description Get booked one time vouchers count from product bookings
 * @returns booked one time voucher tariffs count
 */
export const getBookedOneTimeVoucherTariffsCount = createSelector(
  getBookedOneTimeVoucherTariffs,
  (bookedOneTimeVoucherTariffs: BookingTariff[]) =>
    bookedOneTimeVoucherTariffs.reduce(
      (numberOfBookedOneTimeVoucherTariffs, bookedOneTimeVoucherTariff) =>
        numberOfBookedOneTimeVoucherTariffs + bookedOneTimeVoucherTariff.count,
      0
    )
);

/**
 * Get booked voucher count sorted by voucher type
 * @description Get booked voucher count sorted by voucher type from product bookings
 * @returns Object of voucher type keys and count as value
 */
export const getBookedVoucherCountSortedByVoucherType = createSelector(
  getBookedPromoCodeVoucherTariffsCount,
  getBookedLimitedPromoCodeVoucherTariffsCount,
  getBookedOneTimeVoucherTariffsCount,
  (
    bookedPromoCodeVoucherTariffsCount,
    bookedLimitedPromoCodeVoucherTariffsCount,
    bookedOneTimeVoucherTariffsCount
  ): BookedVoucherCountSortedByVoucherType => ({
    [VoucherType.PromoCode]: bookedPromoCodeVoucherTariffsCount,
    [VoucherType.LimiterPromoCode]: bookedLimitedPromoCodeVoucherTariffsCount,
    [VoucherType.OneTimeVoucher]: bookedOneTimeVoucherTariffsCount
  })
);

/**
 * Get booked boucher tariff by voucher code
 * @description Get booked voucher tariff by provided voucher code
 * @param voucherCode
 * @returns Booked voucher tariff or null
 */
export const getBookedVoucherTariffByVoucherCode = (voucherCode: string) =>
  createSelector(
    getVoucherTariffBookings,
    (bookedTariffs: BookingTariff[]) =>
      bookedTariffs.find((bookedTariff) => bookedTariff.voucherCode === voucherCode) || null
  );

/**
 * Get booked voucher tariff by ticket person id
 * @description Get booked voucher tariff by provided ticket person id
 * @param ticketPersonId
 * @returns Booked voucher tariffs or empty array
 */
export const getVoucherTariffsBookingsByTicketPersonId = (ticketPersonId: number) =>
  createSelector(
    getVoucherTariffBookings,
    (bookedTariffs: BookingTariff[]) =>
      bookedTariffs.filter((bookedTariff) => bookedTariff.ticketPersonId === ticketPersonId) || []
  );

/**
 * Get booked total ticket count per voucher tariff by ticket person id
 * @description Get booked total ticket count per voucher tariff by ticket person id
 * @param ticketPersonId
 * @returns Number of booked voucher tariffs
 */
export const getTotalTicketCountPerVoucherTariffByTicketPersonId = (ticketPersonId: number) =>
  createSelector(getVoucherTariffsBookingsByTicketPersonId(ticketPersonId), (bookedTariffs: BookingTariff[]) =>
    bookedTariffs.reduce((totalCount, bookedTariff) => totalCount + bookedTariff.count, 0)
  );

/**
 * Get remaining ticket count for voucher tariff
 * @description Get remaining voucher tickets count after removing voucher codes ticket count
 * @param voucherCode
 * @param ticketPersonId
 * @returns Number of booked tickets for voucher tariff
 */
export const getRemainingTicketCountForVoucherTariff = (ticketPersonId: number, voucherCode: string) =>
  createSelector(
    getBookedVoucherTariffByVoucherCode(voucherCode),
    getTotalTicketCountPerVoucherTariffByTicketPersonId(ticketPersonId),
    (bookedVoucherTariffByVoucherCode, totalTicketCountPerVoucherTariffByTicketPersonId) => {
      if (!bookedVoucherTariffByVoucherCode) {
        return totalTicketCountPerVoucherTariffByTicketPersonId;
      }

      return totalTicketCountPerVoucherTariffByTicketPersonId - bookedVoucherTariffByVoucherCode.count;
    }
  );

/**
 * Get booked anonymous tariffs
 * @description Get all booked anonymous tariffs from tariffs, vouchers and packages
 * @returns Tariffs or empty array
 */
export const getAnonymousTariffs = createSelector(
  getVoucherTariffBookings,
  (bookedTariffs: BookingTariff[]): BookingTariff[] =>
    bookedTariffs.filter(({ personalizationType }) => personalizationType === TariffPersonalizationType.Anonymous)
);
